import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { userAPI, walletAPI } from '../../services/api';

const WalletManagement = () => {
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [transferData, setTransferData] = useState({
    to_user_id: '',
    amount: '',
  });
  const [loading, setLoading] = useState(true);
  const [transferLoading, setTransferLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const promises = [userAPI.getMyWallet(), walletAPI.getTransactions()];
      
      // Owner, super_admin, admin, and agent can transfer to users they created
      if (['owner', 'super_admin', 'admin', 'agent'].includes(user.user_type)) {
        promises.push(userAPI.listUsers());
      }

      const results = await Promise.all(promises);
      setWallet(results[0].data.wallet);
      setTransactions(results[1].data.transactions);
      if (results[2]) {
        // Backend already filters users based on hierarchy, so use them directly
        setUsers(results[2].data.users);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleTransferChange = (e) => {
    setTransferData({
      ...transferData,
      [e.target.name]: e.target.value,
    });
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    setTransferLoading(true);

    try {
      await walletAPI.transfer({
        to_user_id: parseInt(transferData.to_user_id),
        amount: parseFloat(transferData.amount),
      });
      
      toast.success('Transfer completed successfully!');
      setTransferData({ to_user_id: '', amount: '' });
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.message || 'Transfer failed');
    } finally {
      setTransferLoading(false);
    }
  };

  const transactionColumns = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'transaction_type',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 120,
      renderCell: (params) => `₹${parseFloat(params.value).toFixed(2)}`,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Typography
          variant="body2"
          color={params.value === 'success' ? 'success.main' : 'error.main'}
          sx={{ textTransform: 'capitalize' }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Date',
      width: 180,
      renderCell: (params) => new Date(params.value).toLocaleString(),
    },
    { field: 'notes', headerName: 'Notes', flex: 1 },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <CircularProgress />
      </Box>
    );
  }

  const getWalletDisplayValue = () => {
    if (user.user_type === 'owner') {
      return '∞ (Unlimited)';
    }
    return `₹${parseFloat(wallet?.balance || 0).toFixed(2)}`;
  };

  const getTransferGuideline = () => {
    switch (user.user_type) {
      case 'owner':
        return 'You can transfer to anyone (unlimited balance)';
      case 'super_admin':
        return 'You can transfer to admins, agents, and users you created';
      case 'admin':
        return 'You can transfer to agents you created';
      case 'agent':
        return 'You can transfer to users (players) you created';
      default:
        return 'You do not have transfer permissions';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Typography variant="h4" gutterBottom>
        Wallet Management
      </Typography>

      <Grid container spacing={3}>
        {/* Wallet Info */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Wallet Information
              </Typography>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography>Current Balance:</Typography>
                <Typography variant="h6" color="primary">
                  {getWalletDisplayValue()}
                </Typography>
              </Box>
              {wallet?.max_limit && (
                <>
                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Typography>Wallet Limit:</Typography>
                    <Typography>₹{parseFloat(wallet.max_limit).toFixed(2)}</Typography>
                  </Box>
                  <Box position="relative">
                    <Box
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: 'rgba(255,255,255,0.1)',
                      }}
                    >
                      <Box
                        sx={{
                          height: '100%',
                          borderRadius: 4,
                          backgroundColor: 'primary.main',
                          width: `${Math.min((wallet.balance / wallet.max_limit) * 100, 100)}%`,
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {((wallet.balance / wallet.max_limit) * 100).toFixed(1)}% used
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Transfer Form */}
        {['owner', 'super_admin', 'admin', 'agent'].includes(user.user_type) && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Transfer Balance
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {getTransferGuideline()}
                </Typography>
                <form onSubmit={handleTransfer}>
                  <FormControl fullWidth margin="normal" required>
                    <InputLabel>Select User</InputLabel>
                    <Select
                      name="to_user_id"
                      value={transferData.to_user_id}
                      onChange={handleTransferChange}
                      label="Select User"
                      disabled={transferLoading}
                    >
                      {users.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.username} ({u.user_type}) - Balance: ₹{parseFloat(u.wallet?.balance || 0).toFixed(2)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Amount"
                    name="amount"
                    type="number"
                    value={transferData.amount}
                    onChange={handleTransferChange}
                    margin="normal"
                    required
                    disabled={transferLoading}
                    inputProps={{ step: "0.01", min: "0.01" }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={transferLoading || !transferData.to_user_id || !transferData.amount}
                    sx={{ mt: 2 }}
                  >
                    {transferLoading ? <CircularProgress size={24} /> : 'Transfer'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Transaction History */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Transaction History
          </Typography>
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={transactions}
              columns={transactionColumns}
              pageSize={10}
              rowsPerPageOptions={[10]}
              disableSelectionOnClick
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell': {
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default WalletManagement;
